import React, { useCallback, useEffect, useState } from 'react';
import AdminSegmentToggle from '../admin/AdminSegmentToggle';

export function AccountChangeUsername({ changeUsername, currentUsername, embedded = false }) {
  const [newUsername, setNewUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const res = await changeUsername(currentPassword, newUsername);
      if (!res.ok) {
        setError(res.error || 'Could not update username.');
        return;
      }
      setCurrentPassword('');
      setNewUsername('');
      setSuccess(`Username updated to “${res.username}”. Use it next time you sign in.`);
    } finally {
      setBusy(false);
    }
  };

  const body = (
        <>
        {!embedded ? (
          <p className="account-password-card__lead small text-muted mb-3">
            Current username: <strong>{currentUsername}</strong>. New names use lowercase letters, numbers,
            underscores, or hyphens (3–32 characters).
          </p>
        ) : (
          <p className="customer-account__inline-lead small mb-3">
            Signed in as <strong>{currentUsername}</strong>. Use lowercase letters, numbers, underscores, or hyphens
            (3–32 characters).
          </p>
        )}
        <form onSubmit={onSubmit} noValidate className="customer-account__form account-password-form">
          {error ? (
            <div className="alert alert-danger py-2 small" role="alert">
              {error}
            </div>
          ) : null}
          {success ? (
            <div className="alert alert-success py-2 small" role="status">
              {success}
            </div>
          ) : null}
          <div className="checkout-field mb-3">
            <label className="checkout-field__label" htmlFor="account-new-username">
              New username
            </label>
            <input
              id="account-new-username"
              type="text"
              className="form-control checkout-input"
              autoComplete="username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              disabled={busy}
              minLength={3}
              maxLength={32}
              pattern="[a-zA-Z0-9][a-zA-Z0-9_-]*"
              required
            />
          </div>
          <div className="checkout-field mb-3">
            <label className="checkout-field__label" htmlFor="account-username-password">
              Current password <span className="checkout-field__req">*</span>
            </label>
            <input
              id="account-username-password"
              type="password"
              className="form-control checkout-input"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={busy}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={busy}>
            {busy ? 'Updating…' : 'Update username'}
          </button>
        </form>
        </>
  );

  if (embedded) return body;

  return (
    <section className="account-password account-username" aria-labelledby="account-username-heading">
      <h2 id="account-username-heading" className="account-section-label">
        Username
      </h2>
      <div className="account-password-card">{body}</div>
    </section>
  );
}

export function AccountAdminUsers({ listUsers, createUser, updateUserRole, currentUsername, embedded = false }) {
  const [users, setUsers] = useState([]);
  const [rolesSupported, setRolesSupported] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [roleBusyId, setRoleBusyId] = useState(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('member');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const res = await listUsers();
    setLoading(false);
    if (!res.ok) {
      setLoadError(res.error || 'Could not load users.');
      setUsers([]);
      return;
    }
    setUsers(res.users);
    setRolesSupported(res.rolesSupported !== false);
  }, [listUsers]);

  const onRoleChange = async (userId, nextRole) => {
    setRoleBusyId(userId);
    const res = await updateUserRole(userId, nextRole);
    setRoleBusyId(null);
    if (!res.ok) {
      setFormError(res.error || 'Could not update role.');
      return;
    }
    setFormError('');
    await reload();
  };

  useEffect(() => {
    void reload();
  }, [reload]);

  const onCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setBusy(true);
    try {
      const res = await createUser(username, password, confirmPassword, role);
      if (!res.ok) {
        setFormError(res.error || 'Could not create user.');
        return;
      }
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setRole('member');
      setFormSuccess(
        `Created “${res.username}” as ${res.role === 'admin' ? 'administrator' : 'team member'}. They can sign in with that password.`
      );
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const administrators = users.filter((u) => u.role === 'admin');
  const members = users.filter((u) => u.role === 'member');

  const renderStaffTable = (groupUsers, groupLabel) => {
    if (groupUsers.length === 0) return null;
    return (
      <div className="account-users-group" key={groupLabel}>
        <h3 className="account-users-group__title">{groupLabel}</h3>
        <div className="table-responsive account-users-table-wrap">
          <table className="table table-sm account-users-table mb-0">
            <thead>
              <tr>
                <th scope="col">Username</th>
                <th scope="col">Access</th>
              </tr>
            </thead>
            <tbody>
              {groupUsers.map((u) => (
                <tr key={u.id} className={u.username === currentUsername ? 'account-users-table__you' : ''}>
                  <td>
                    {u.username}
                    {u.username === currentUsername ? (
                      <span className="account-users-table__badge">you</span>
                    ) : null}
                  </td>
                  <td>
                    {rolesSupported && u.username !== currentUsername ? (
                      <AdminSegmentToggle
                        size="sm"
                        className="account-users-role-select-wrap"
                        ariaLabel={`Role for ${u.username}`}
                        value={u.role}
                        disabled={roleBusyId === u.id}
                        onChange={(v) => void onRoleChange(u.id, v)}
                        options={[
                          { value: 'admin', label: 'Admin' },
                          { value: 'member', label: 'Member' },
                        ]}
                      />
                    ) : (
                      <span className="account-users-role-label">
                        {u.role === 'admin' ? 'Administrator' : 'Team member'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const body = (
      <>
      <div className={embedded ? 'account-users-embedded__list' : 'account-password-card account-users-card mb-4'}>
        <p className={embedded ? 'customer-account__inline-lead small mb-3' : 'account-password-card__lead small text-muted mb-3'}>
          Manage <strong>store staff</strong> only. <strong>Administrators</strong> use catalog admin and orders.{' '}
          <strong>Team members</strong> can sign in but do not get catalog access. Shop customers register on the
          storefront — they are not listed here.
        </p>
        {!rolesSupported ? (
          <div className="alert alert-warning py-2 small mb-3" role="alert">
            Roles are not enabled in your database yet. Run{' '}
            <code>php scripts/apply-admin-roles-migration.php</code> then refresh. Until then, every account
            behaves as an administrator.
          </div>
        ) : null}
        {loading ? (
          <p className="small text-muted mb-0">Loading team…</p>
        ) : loadError ? (
          <div className="alert alert-warning py-2 small mb-0" role="alert">
            {loadError}
          </div>
        ) : users.length === 0 ? (
          <p className="small text-muted mb-0">No team accounts yet. Add staff below.</p>
        ) : (
          <>
            {renderStaffTable(administrators, 'Administrators')}
            {renderStaffTable(members, 'Team members')}
            {administrators.length === 1 && members.length === 0 ? (
              <p className="account-users-solo-hint small text-muted mb-0 mt-3">
                You are the only administrator. Add a team member for limited access, or another administrator
                for backup.
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className={embedded ? 'account-users-embedded__create account-card account-card--nested' : 'account-password-card'}>
        <h3 className="h6 mb-3 account-users-form-title">Add team member</h3>
        <form onSubmit={onCreate} noValidate className={`account-password-form${embedded ? ' customer-account__form' : ''}`}>
          {formError ? (
            <div className="alert alert-danger py-2 small" role="alert">
              {formError}
            </div>
          ) : null}
          {formSuccess ? (
            <div className="alert alert-success py-2 small" role="status">
              {formSuccess}
            </div>
          ) : null}
          <div className={embedded ? 'checkout-field' : 'mb-3'}>
            <label className={embedded ? 'checkout-field__label' : 'form-label small'} htmlFor="new-user-username">
              Username
            </label>
            <input
              id="new-user-username"
              type="text"
              className={embedded ? 'form-control checkout-input' : 'form-control'}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={busy}
              minLength={3}
              maxLength={32}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label small" htmlFor="new-user-role">
              Role
            </label>
            <AdminSegmentToggle
              ariaLabel="New user role"
              value={role}
              onChange={setRole}
              disabled={busy}
              options={[
                { value: 'member', label: 'Team member' },
                { value: 'admin', label: 'Administrator' },
              ]}
            />
            <p className="small text-muted mb-0 mt-1">
              Team members cannot open catalog admin. Administrators have full access.
            </p>
          </div>
          <div className="mb-3">
            <label className="form-label small" htmlFor="new-user-password">
              Password
            </label>
            <input
              id="new-user-password"
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              minLength={8}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label small" htmlFor="new-user-confirm">
              Confirm password
            </label>
            <input
              id="new-user-confirm"
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={busy}
              minLength={8}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={busy}>
            {busy ? 'Creating…' : 'Create user'}
          </button>
        </form>
      </div>
      </>
  );

  if (embedded) return body;

  return (
    <section className="account-users" aria-labelledby="account-users-heading">
      <h2 id="account-users-heading" className="account-section-label">
        Team accounts
      </h2>
      {body}
    </section>
  );
}
